import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { authedLayoutRoute } from './authed.js';
import {
  approvePayrollRun,
  calculatePayrollRun,
  deletePayrollRun,
  downloadPayslipPdf,
  getPayrollRun,
  voidPayrollRun,
} from '../api/payrollRuns.js';
import { listComplianceReports } from '../api/complianceReports.js';
import { downloadBankFile } from '../api/bankExport.js';
import { MissingBankInfoModal } from '../components/MissingBankInfoModal.js';
import type { MissingBankInfoEmployee } from '@kora/types';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice, apiErrorMessage } from '../components/ApiErrorNotice.js';
import { DownloadButton as ComplianceDownloadButton } from './complianceReports.js';
import { PayrollRunStatusPill } from '../components/PayrollRunStatusPill.js';
import { ReasonModal } from '../components/ReasonModal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { formatDate, formatMoney, formatNumber, formatPeriod } from '../lib/format.js';
import type {
  PayrollRunResponse,
  PayslipBreakdownWire,
  PayslipContributionLine,
  PayslipResponse,
} from '@kora/types';
import { PayslipBreakdownWire as PayslipBreakdownWireSchema } from '@kora/types';

/**
 * /payroll-runs/:id — detail + all lifecycle actions.
 *
 * Actions available per status (mirrors server state machine):
 *   draft        → Calculate | Void
 *   calculating  → (none; transient — refetch shows `calculated`)
 *   calculated   → Approve | Void
 *   approved     → Void | Correct (creates v2 via /new?supersedesId=...)
 *   voided       → Correct (creates v2 via /new?supersedesId=...)
 *   paid         → (read-only — payment-reversal is a separate flow)
 */

export const payrollRunDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/payroll-runs/$id',
  component: PayrollRunDetailPage,
});

function PayrollRunDetailPage(): JSX.Element {
  const { id } = payrollRunDetailRoute.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['payroll-runs', 'detail', id],
    queryFn: () => getPayrollRun(id, { includePayslips: true }),
  });

  // ---- Action mutations ----

  async function refreshViews(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['payroll-runs', 'detail', id] }),
      queryClient.invalidateQueries({ queryKey: ['payroll-runs', 'list'] }),
    ]);
  }

  const calculateMutation = useMutation({
    mutationFn: () => calculatePayrollRun(id),
    onSuccess: refreshViews,
  });

  const [approveOpen, setApproveOpen] = useState(false);
  const approveMutation = useMutation({
    mutationFn: (reason: string) => approvePayrollRun(id, { reason }),
    onSuccess: async () => {
      await refreshViews();
      setApproveOpen(false);
    },
  });

  const [voidOpen, setVoidOpen] = useState(false);
  const voidMutation = useMutation({
    mutationFn: (reason: string) => voidPayrollRun(id, { reason }),
    onSuccess: async () => {
      await refreshViews();
      setVoidOpen(false);
    },
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => deletePayrollRun(id),
    onSuccess: async () => {
      // Run is gone — refetch the list, bounce to it (no point staying on
      // a detail page for something that no longer exists).
      await queryClient.invalidateQueries({ queryKey: ['payroll-runs', 'list'] });
      await queryClient.removeQueries({ queryKey: ['payroll-runs', 'detail', id] });
      await navigate({ to: '/payroll-runs' });
    },
  });

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <nav className="text-xs text-gray-500">
        <Link to="/payroll-runs" className="hover:underline">
          Payroll runs
        </Link>{' '}
        / {data ? `${formatDate(data.periodStart)} → ${formatDate(data.periodEnd)}` : id}
      </nav>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} /> : null}

      {data ? (
        <>
          <RunHeader
            run={data}
            onCalculate={() => {
              calculateMutation.reset();
              calculateMutation.mutate();
            }}
            calculating={calculateMutation.isPending}
            calculateError={calculateMutation.error}
            onApprove={() => {
              approveMutation.reset();
              setApproveOpen(true);
            }}
            onVoid={() => {
              voidMutation.reset();
              setVoidOpen(true);
            }}
            onCorrect={() =>
              void navigate({
                to: '/payroll-runs/new',
                search: { supersedesId: data.id },
              })
            }
            onDelete={() => {
              deleteMutation.reset();
              setDeleteOpen(true);
            }}
          />

          <RunSummary run={data} />

          {data.payslips && data.payslips.length > 0 ? (
            <PayslipsTable runId={data.id} payslips={data.payslips} />
          ) : null}

          <RunCompliancePanel runId={data.id} status={data.status} />

          <BankExportSection runId={data.id} status={data.status} payslips={data.payslips ?? []} />

          <ReasonModal
            open={approveOpen}
            title="Approve this payroll run"
            description="The calculated totals are locked in. Approved runs can only be changed by superseding them with a corrected new version (use &quot;Correct this run&quot;)."
            confirmLabel="Approve"
            confirmVariant="primary"
            isSubmitting={approveMutation.isPending}
            errorMessage={apiErrorMessage(approveMutation.error, 'Approval failed.')}
            onCancel={() => setApproveOpen(false)}
            onConfirm={(reason) => approveMutation.mutate(reason)}
          />

          <ReasonModal
            open={voidOpen}
            title="Void this payroll run"
            description="Voided runs are preserved for audit but excluded from &quot;what's current.&quot; This cannot be undone. To redo the same period, use &quot;Correct this run&quot; on the voided row to create a corrected version."
            confirmLabel="Void"
            confirmVariant="danger"
            isSubmitting={voidMutation.isPending}
            errorMessage={apiErrorMessage(voidMutation.error, 'Void failed.')}
            onCancel={() => setVoidOpen(false)}
            onConfirm={(reason) => voidMutation.mutate(reason)}
          />

          <ConfirmDialog
            open={deleteOpen}
            title="Delete this draft"
            description="Drafts are disposable — no payslips were created yet, no audit chain of custody. The period + version will be free to reuse. This cannot be undone."
            confirmLabel="Delete draft"
            confirmVariant="danger"
            isSubmitting={deleteMutation.isPending}
            errorMessage={apiErrorMessage(deleteMutation.error, 'Delete failed.')}
            onCancel={() => setDeleteOpen(false)}
            onConfirm={() => deleteMutation.mutate()}
          />
        </>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Header: title + status + action row
// ---------------------------------------------------------------------------

function RunHeader({
  run,
  onCalculate,
  calculating,
  calculateError,
  onApprove,
  onVoid,
  onCorrect,
  onDelete,
}: {
  run: PayrollRunResponse;
  onCalculate: () => void;
  calculating: boolean;
  calculateError: unknown;
  onApprove: () => void;
  onVoid: () => void;
  onCorrect: () => void;
  onDelete: () => void;
}): JSX.Element {
  const canCalculate = run.status === 'draft';
  const canApprove = run.status === 'calculated';
  const canVoid = run.status === 'calculated' || run.status === 'approved';
  // Approved AND voided runs are both supersedable — they're the
  // terminal states that occupy the (period, version) slot. Only
  // path to a corrected v2 if the original was already finalised.
  const canCorrect = run.status === 'approved' || run.status === 'voided';
  // Delete is only available for drafts — same server constraint.
  // Calculated/approved/voided runs are preserved for audit; use Void instead.
  const canDelete = run.status === 'draft';

  return (
    <header className="space-y-3">
      <h1 className="font-serif text-3xl">
        Payroll · {formatDate(run.periodStart)} → {formatDate(run.periodEnd)}
      </h1>
      <div className="flex items-center gap-3 text-sm text-gray-400">
        <PayrollRunStatusPill status={run.status} />
        <span>·</span>
        <span>v{run.version}</span>
        {run.supersedesId ? (
          <>
            <span>·</span>
            <span>
              supersedes{' '}
              <Link
                to="/payroll-runs/$id"
                params={{ id: run.supersedesId }}
                className="font-mono text-xs hover:underline"
              >
                {run.supersedesId}
              </Link>
            </span>
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        {canCalculate ? (
          <button
            type="button"
            onClick={onCalculate}
            disabled={calculating}
            className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {calculating ? 'Calculating...' : 'Calculate'}
          </button>
        ) : null}
        {canApprove ? (
          <button
            type="button"
            onClick={onApprove}
            className="rounded bg-green-600 text-white px-4 py-2 text-sm font-medium hover:bg-green-500"
          >
            Approve
          </button>
        ) : null}
        {canCorrect ? (
          <button
            type="button"
            onClick={onCorrect}
            className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900"
          >
            Correct (new version)
          </button>
        ) : null}
        {canVoid ? (
          <button
            type="button"
            onClick={onVoid}
            className="rounded border border-red-800 text-red-400 px-4 py-2 text-sm hover:bg-red-900/40"
          >
            Void
          </button>
        ) : null}
        {canDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className="rounded border border-red-800 text-red-400 px-4 py-2 text-sm hover:bg-red-900/40"
          >
            Delete draft
          </button>
        ) : null}
      </div>

      <ApiErrorNotice error={calculateError} fallback="Calculation failed." />
    </header>
  );
}

// ---------------------------------------------------------------------------
// Summary: totals + metadata
// ---------------------------------------------------------------------------

function RunSummary({ run }: { run: PayrollRunResponse }): JSX.Element {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="rounded border border-gray-900 p-4 space-y-2">
        <h3 className="text-xs uppercase tracking-wide text-gray-400">Period</h3>
        <KV k="Period start" v={formatDate(run.periodStart)} />
        <KV k="Period end" v={formatDate(run.periodEnd)} />
        <KV k="Pay date" v={formatDate(run.payDate)} />
        <KV k="Version" v={`v${run.version}`} />
      </div>

      <div className="rounded border border-gray-900 p-4 space-y-2">
        <h3 className="text-xs uppercase tracking-wide text-gray-400">
          Totals
          <span className="ml-2 normal-case tracking-normal text-gray-600">
            (in {run.totals?.currency ?? 'base currency'})
          </span>
        </h3>
        {run.totals ? (
          <>
            <KV k="Gross" v={formatMoney(run.totals.grossTotal, run.totals.currency)} mono />
            <KV k="Income tax" v={formatMoney(run.totals.taxTotal, run.totals.currency)} mono />
            <KV
              k="Employee contributions"
              v={formatMoney(run.totals.employeeSocialSecurityTotal, run.totals.currency)}
              mono
            />
            <KV
              k="Employer contributions"
              v={formatMoney(run.totals.employerSocialSecurityTotal, run.totals.currency)}
              mono
            />
            <KV k="Net" v={formatMoney(run.totals.netTotal, run.totals.currency)} mono bold />
            <KV k="Employees" v={formatNumber(run.totals.employeeCount)} />
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Totals populate after calculation.
          </p>
        )}
      </div>

      <div className="rounded border border-gray-900 p-4 space-y-2 sm:col-span-2">
        <h3 className="text-xs uppercase tracking-wide text-gray-400">Provenance</h3>
        <KV k="Rule spec hash" v={run.ruleSpecHash} mono />
        <KV k="Interpreter" v={run.interpreterVersion} mono />
        <KV k="Idempotency key" v={run.idempotencyKey} mono />
        {run.approvedAt ? (
          <>
            <KV k="Approved at" v={run.approvedAt.slice(0, 19).replace('T', ' ')} />
            <KV k="Approval reason" v={run.approvalReason ?? '—'} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function KV({
  k,
  v,
  mono,
  bold,
}: {
  k: string;
  v: string;
  mono?: boolean;
  bold?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-gray-500">{k}</span>
      <span
        className={`${mono ? 'font-mono text-xs break-all text-right' : ''} ${
          bold ? 'text-white font-medium' : 'text-gray-300'
        }`}
      >
        {v}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payslips table
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ADR-0019: the pay-currency view off a payslip's breakdown (USD on a CDF run),
// or null for a same-currency employee. Drives the net display + bank currency.
function payCurrencyOf(p: PayslipResponse): { currency: string; takeHome: string } | null {
  const block = (p.breakdown as { payCurrency?: { payCurrency?: string; takeHome?: string } } | null | undefined)?.payCurrency;
  return block?.payCurrency && block.takeHome != null ? { currency: block.payCurrency, takeHome: block.takeHome } : null;
}

// Bank export — one download button per distinct currency in the run
// ---------------------------------------------------------------------------

function BankExportSection({
  runId,
  status,
  payslips,
}: {
  runId: string;
  status: string;
  payslips: PayslipResponse[];
}): JSX.Element | null {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<{ currency: string; employees: MissingBankInfoEmployee[] } | null>(null);

  // Available only for finalized runs (mirrors the server gate).
  if (status !== 'approved' && status !== 'paid') return null;

  // ADR-0019: bank files are per PAY currency (a USD-salaried employee → USD file).
  const currencies = Array.from(new Set(payslips.map((p) => payCurrencyOf(p)?.currency ?? p.currency))).sort();
  if (currencies.length === 0) return null;

  const handleDownload = async (currency: string): Promise<void> => {
    setBusy(currency);
    setError(null);
    try {
      await downloadBankFile(runId, currency);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; missingEmployees?: MissingBankInfoEmployee[] } | null) : null;
      if (err instanceof ApiError && err.status === 422 && body?.error === 'EXPORT_BLOCKED_MISSING_BANK_INFO') {
        setMissing({ currency, employees: body.missingEmployees ?? [] });
      } else {
        setError(err instanceof Error ? err.message : 'Download failed. Try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <header className="bg-gray-950 px-4 py-3">
        <div className="text-xs uppercase tracking-wide text-gray-400">Bank export</div>
      </header>
      <div className="p-4 space-y-3">
        <p className="text-sm text-gray-400">
          Download a generic salary CSV to upload to your bank&apos;s bulk payments portal. One file per currency.
        </p>
        <div className="flex flex-wrap gap-2">
          {currencies.map((currency) => (
            <button
              key={currency}
              type="button"
              onClick={() => void handleDownload(currency)}
              disabled={busy !== null}
              className="rounded border border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-900 disabled:opacity-50"
            >
              {busy === currency ? 'Preparing…' : `Download bank file (${currency})`}
            </button>
          ))}
        </div>
        {error ? <Alert variant="error">{error}</Alert> : null}
      </div>
      {missing ? (
        <MissingBankInfoModal
          missingEmployees={missing.employees}
          currency={missing.currency}
          onClose={() => setMissing(null)}
        />
      ) : null}
    </div>
  );
}

function PayslipsTable({
  runId,
  payslips,
}: {
  runId: string;
  payslips: PayslipResponse[];
}): JSX.Element {
  // Default: show everything expanded when the list is small enough to not
  // drown the page. For large runs (50+ payslips) default-collapsed keeps
  // the page scannable — operators toggle in as needed. Threshold picked
  // to cover Mokoafrika-size orgs (45 employees) while protecting
  // bigger tenants from a 200-row wall of scheme detail.
  const DEFAULT_EXPANDED_THRESHOLD = 10;
  const [expanded, setExpanded] = useState<Set<string>>(
    () =>
      new Set(
        payslips.length <= DEFAULT_EXPANDED_THRESHOLD
          ? payslips.map((p) => p.id)
          : [],
      ),
  );
  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const expandAll = (): void => setExpanded(new Set(payslips.map((p) => p.id)));
  const collapseAll = (): void => setExpanded(new Set());
  const allOpen = expanded.size === payslips.length;

  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <header className="bg-gray-950 px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-wide text-gray-400">
          Payslips
          <span className="ml-2 normal-case tracking-normal text-gray-600">
            · click a row to toggle per-scheme breakdown
          </span>
        </div>
        <button
          type="button"
          onClick={allOpen ? collapseAll : expandAll}
          className="text-xs border border-gray-700 rounded px-2 py-1 text-gray-300 hover:bg-gray-900"
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-left text-gray-400 uppercase text-xs">
          <tr>
            <th className="px-4 py-3 font-medium w-8"></th>
            <th className="px-4 py-3 font-medium">Employee</th>
            <th className="px-4 py-3 font-medium">Country</th>
            <th className="px-4 py-3 font-medium text-right">Gross</th>
            <th className="px-4 py-3 font-medium text-right">Income tax</th>
            <th className="px-4 py-3 font-medium text-right">Contributions</th>
            <th className="px-4 py-3 font-medium text-right">Net</th>
            <th className="px-4 py-3 font-medium w-20"></th>
          </tr>
        </thead>
        <tbody>
          {payslips.map((p) => {
            const isOpen = expanded.has(p.id);
            return (
              <>
                <tr
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  aria-expanded={isOpen}
                  className={`border-t border-gray-900 cursor-pointer hover:bg-gray-900/60 ${
                    isOpen ? 'bg-gray-950' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-base">
                    <span
                      className={`inline-block transition-transform ${
                        isOpen ? 'rotate-90 text-white' : 'text-gray-500'
                      }`}
                      aria-hidden="true"
                    >
                      ▸
                    </span>
                  </td>
                  {/* Stack number + name in one cell so the table doesn't
                      grow another column. Number stays mono+small (same
                      visual weight as before for muscle memory); name is
                      the primary label below it. */}
                  <td className="px-4 py-3">
                    <div className="text-white">{formatEmployeeName(p)}</div>
                    <div className="font-mono text-xs text-gray-500">
                      {p.employeeNumber}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{p.employeeCountry}</td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    {formatMoney(p.grossSalary, p.currency)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    {formatMoney(p.taxAmount, p.currency)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-right">
                    {formatMoney(p.socialSecurity, p.currency)}
                  </td>
                  <td className="px-4 py-3 text-xs text-white text-right font-medium">
                    {(() => {
                      const pay = payCurrencyOf(p);
                      return pay ? (
                        <>
                          {formatMoney(pay.takeHome, pay.currency as typeof p.currency)}
                          <div className="text-[0.65rem] text-gray-500 font-normal">statutory {formatMoney(p.netSalary, p.currency)}</div>
                        </>
                      ) : (
                        formatMoney(p.netSalary, p.currency)
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <PayslipPdfButton runId={runId} payslipId={p.id} />
                  </td>
                </tr>
                {isOpen ? <PayslipBreakdownRow key={`${p.id}-bd`} payslip={p} /> : null}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compliance reports panel (PR 6a-ui)
// ---------------------------------------------------------------------------

/**
 * Shows the compliance report(s) linked to this run. Only rendered for
 * `approved`/`paid` runs — earlier states have no report yet, and an empty
 * panel would just confuse. The backend has no payrollRunId list filter
 * (PR 6a), so we fetch the org's reports and filter client-side.
 */
function RunCompliancePanel({
  runId,
  status,
}: {
  runId: string;
  status: PayrollRunResponse['status'];
}): JSX.Element | null {
  const showPanel = status === 'approved' || status === 'paid';
  const { data, isLoading } = useQuery({
    queryKey: ['compliance-reports', 'by-run', runId],
    // Server-side filter (PR ⑥b) — the backend now supports payrollRunId,
    // so we no longer fetch the whole org list and filter client-side.
    queryFn: () => listComplianceReports({ payrollRunId: runId }),
    enabled: showPanel,
  });

  if (!showPanel) return null;

  const reports = data?.reports ?? [];
  if (reports.length > 1) {
    // payrollRunId is @unique on the backend, so this shouldn't happen.
    // eslint-disable-next-line no-console
    console.warn(`Run ${runId} has ${reports.length} compliance reports; expected at most 1.`);
  }

  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <header className="bg-gray-950 px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        Compliance reports
      </header>
      <div className="p-4 text-sm">
        {isLoading ? (
          <p className="text-gray-500">Loading…</p>
        ) : reports.length === 0 ? (
          <p className="text-gray-500">
            No compliance report — this country&apos;s regulator format ships in PR ⑥b/⑥c.
          </p>
        ) : (
          <ul className="space-y-2">
            {reports.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3">
                <Link
                  to="/compliance-reports/$id"
                  params={{ id: r.id }}
                  className="hover:underline"
                >
                  {formatPeriod(r.periodYear, r.periodMonth)} · {r.regulatorFormat} ·{' '}
                  <span className="text-gray-500">generated {formatDate(r.generatedAt)}</span>
                </Link>
                <ComplianceDownloadButton id={r.id} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Per-row "PDF" button. Stops click propagation so clicking the
 * button doesn't toggle the row's per-scheme breakdown expansion.
 * Optimistic UX: shows "..." while the download is in flight,
 * surfaces a small inline error if the request fails.
 */
function PayslipPdfButton({
  runId,
  payslipId,
}: {
  runId: string;
  payslipId: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="flex items-center justify-end gap-2">
      {err ? (
        <span className="text-xs text-red-400" role="alert" title={err}>
          Failed
        </span>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={async (e) => {
          e.stopPropagation();
          setErr(null);
          setBusy(true);
          try {
            await downloadPayslipPdf(runId, payslipId);
          } catch (caught) {
            setErr(
              caught instanceof ApiError
                ? caught.message
                : 'Download failed. Try again.',
            );
          } finally {
            setBusy(false);
          }
        }}
        className="text-xs border border-gray-700 rounded px-2 py-1 text-gray-300 hover:bg-gray-900 disabled:opacity-50"
        aria-label={`Download PDF payslip for ${payslipId}`}
      >
        {busy ? '…' : 'PDF'}
      </button>
    </div>
  );
}

function PayslipBreakdownRow({ payslip: p }: { payslip: PayslipResponse }): JSX.Element {
  const breakdown = parseBreakdown(p.breakdown);
  return (
    <tr className="bg-gray-950/40">
      <td></td>
      <td colSpan={7} className="px-4 py-3">
        <div className="space-y-2 text-xs">
          <div>
            <p className="uppercase tracking-wide text-gray-500 text-[0.65rem] mb-1">
              Contributions by scheme
            </p>
            {breakdown && breakdown.contributions.length > 0 ? (
              <ContributionsBreakdown
                contributions={breakdown.contributions}
                currency={p.currency}
              />
            ) : (
              <p className="text-gray-600">No per-scheme detail available.</p>
            )}
          </div>

          {breakdown ? (
            <div>
              <p className="uppercase tracking-wide text-gray-500 text-[0.65rem] mb-1">
                Employer social security (total)
              </p>
              <p className="font-mono text-gray-300">
                {formatMoney(breakdown.employerSocialSecurity, p.currency)}
              </p>
            </div>
          ) : null}

          {p.fxConversion ? (
            <div>
              <p className="uppercase tracking-wide text-gray-500 text-[0.65rem] mb-1">
                FX conversion
              </p>
              <p className="font-mono text-gray-400">
                1 {p.fxConversion.fromCurrency} = {p.fxConversion.rate}{' '}
                {p.fxConversion.toCurrency}
              </p>
            </div>
          ) : null}

          <div>
            <p className="uppercase tracking-wide text-gray-500 text-[0.65rem] mb-1">
              Rule spec hash
            </p>
            <p className="font-mono text-gray-500 break-all">{p.ruleSpecHash}</p>
          </div>
        </div>
      </td>
    </tr>
  );
}

function ContributionsBreakdown({
  contributions,
  currency,
}: {
  contributions: PayslipContributionLine[];
  currency: PayslipResponse['currency'];
}): JSX.Element {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-600 text-[0.65rem] uppercase tracking-wide">
          <th className="text-left font-medium py-1">Scheme</th>
          <th className="text-right font-medium py-1">Employee</th>
          <th className="text-right font-medium py-1">Employer</th>
        </tr>
      </thead>
      <tbody>
        {contributions.map((c) => (
          <tr key={c.scheme} className="border-t border-gray-900">
            <td className="py-1 text-gray-300">{c.scheme}</td>
            <td className="py-1 text-right font-mono">
              {formatMoney(c.employeePeriod, currency)}
            </td>
            <td className="py-1 text-right font-mono">
              {formatMoney(c.employerPeriod, currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Runtime-validate the untyped `breakdown` field from the API. */
function parseBreakdown(raw: unknown): PayslipBreakdownWire | null {
  const parsed = PayslipBreakdownWireSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Render an employee's display name from a payslip row. Middle name is
 * included when present; we collapse extra whitespace so a missing
 * middle name doesn't leave a double-space.
 */
function formatEmployeeName(p: {
  employeeFirstName: string;
  employeeMiddleName: string | null;
  employeeLastName: string;
}): string {
  return [p.employeeFirstName, p.employeeMiddleName, p.employeeLastName]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Loading + error states
// ---------------------------------------------------------------------------

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading...
    </div>
  );
}

function ErrorState({ error }: { error: unknown }): JSX.Element {
  const is404 = error instanceof ApiError && error.status === 404;
  return (
    <Alert
      variant="error"
      title={is404 ? 'Payroll run not found' : 'Could not load payroll run'}
      action={
        <Link
          to="/payroll-runs"
          className="inline-block rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Back to payroll runs
        </Link>
      }
    >
      {apiErrorMessage(error, 'Unknown error')}
    </Alert>
  );
}
